import {
   LocationPickerButton,
   useLocationPickerTracking,
} from '@ifixit/ui/components/location-picker-trigger';
import { useLocationPicker, useUserLocale } from '@ifixit/ui/components/location-provider';
import type { MouseEvent, ReactNode } from 'react';
import { MountOnFirstOpen, prefetch } from 'Shared/mount_on_first_open';

const loadSheet = () => import('./header_location_picker_sheet');

export interface LocationPickerTriggerProps {
   'data-testid': string;
   'data-user-locale': string;
   'aria-haspopup': 'dialog';
   'aria-expanded': boolean;
   onClick: (event: MouseEvent<HTMLButtonElement>) => void;
   onFocus: () => void;
   onPointerEnter: () => void;
}

interface HeaderLocationPickerTriggerProps {
   /** Renders the button that opens the picker; the globe button if omitted. */
   trigger?: (props: LocationPickerTriggerProps) => ReactNode;
}

/**
 * The header's location picker. The trigger renders with the header; the
 * sheet is fetched when someone points at or focuses the trigger, and mounted
 * the first time the picker opens, so a page view that never opens it never
 * runs it.
 */
export function HeaderLocationPicker({ trigger }: HeaderLocationPickerTriggerProps) {
   const { isPickerOpen, closePicker } = useLocationPicker();

   return (
      <>
         <HeaderLocationPickerTrigger trigger={trigger} />
         <MountOnFirstOpen
            isOpen={isPickerOpen}
            load={loadSheet}
            props={{}}
            onLoadFailed={closePicker}
         />
      </>
   );
}

/**
 * A second trigger for the sheet a HeaderLocationPicker under the same
 * LocationProvider mounts. Every HeaderLocationPicker mounts its own sheet
 * on the provider's one open flag, so a second one opens two sheets at once.
 * Sends the same events, in the same order, as LocationPicker's SheetTrigger.
 */
export function HeaderLocationPickerTrigger({ trigger }: HeaderLocationPickerTriggerProps) {
   const [userLocale] = useUserLocale();
   const { isPickerOpen, openPicker } = useLocationPicker();
   const tracking = useLocationPickerTracking();

   const triggerProps: LocationPickerTriggerProps = {
      'data-testid': 'location-picker-trigger',
      'data-user-locale': userLocale,
      'aria-haspopup': 'dialog',
      'aria-expanded': isPickerOpen,
      onClick: () => {
         tracking.trackToggleClick();
         tracking.trackOpened();
         openPicker();
      },
      onFocus: () => prefetch(loadSheet),
      onPointerEnter: () => prefetch(loadSheet),
   };

   return trigger ? trigger(triggerProps) : <LocationPickerButton {...triggerProps} />;
}
